import React, { useMemo } from 'react';
import { ShiftRecord } from '../../../../server/src/types/whenIWork';
import './IndividualTimeline.css';
import { useClassroomData, processedClassroomData } from '../../data/classroomData';

// Interfaces for timeline data
export interface PersonTimeSegment {
  startTime: Date;
  endTime: Date;
  status: 'present' | 'absent' | 'break';
  prevClassroom?: {id: string, name: string, status: 'present' | 'break'};
  nextClassroom?: {id: string, name: string, status: 'present' | 'break'};
}

export interface PersonTimeline {
  id: string;
  name: string;
  role: 'student' | 'teacher';
  timeSegments: PersonTimeSegment[];
  wiw_shift?: ShiftRecord[];
  shiftMarkers?: {
    starts: Array<{
      time: Date;
      isEstimated: boolean;
    }>;
    ends: Array<{
      time: Date;
      isEstimated: boolean;
    }>;
  };
}

interface IndividualTimelineProps {
  person: PersonTimeline;
  timeRange: { start: Date; end: Date };
  onTransitionClick?: (classroomId: string, teacherId: string) => void;
  highlightRef?: React.RefObject<HTMLDivElement> | React.RefCallback<HTMLDivElement>;
  isHighlighted?: boolean;
}

const IndividualTimeline: React.FC<IndividualTimelineProps> = ({ 
  person, 
  timeRange,
  onTransitionClick,
  highlightRef,
  isHighlighted
}) => {
  // Calculate the time span
  const startHour = timeRange.start.getHours();
  const endHour = timeRange.end.getHours();
  const totalHours = endHour - startHour;
  const totalMinutes = totalHours * 60;
  const classroomData = useClassroomData().data;
  const { sections } = useMemo(()=> processedClassroomData(classroomData), [classroomData])
  
  // Calculate positions for shift markers if they exist
  const calculateMarkerPosition = (time: Date) => {
    const markerHour = time.getHours();
    const markerMinute = time.getMinutes();
    const minutesSinceRangeStart = (markerHour - timeRange.start.getHours()) * 60 + markerMinute;
    return Math.max(0, Math.min(100, (minutesSinceRangeStart / totalMinutes) * 100));
  };

  // Handler for transition marker clicks
  const handleTransitionClick = (classroomId: string, teacherId: string) => {
    // Check if this is a transition from the Other section to a regular classroom
    // or if this is a transition to the Other section
    if (onTransitionClick) {
      // If the classroom ID is from one of the special "Other" section classrooms,
      // we should prefix it to make it identifiable
      if (classroomId === 'other' || classroomId.startsWith('other-')) {
        onTransitionClick('other-section', teacherId);
      } else {
        onTransitionClick(classroomId, teacherId);
      }
    }
  };

  return (
    <div 
      className={`classroom-row individual-row ${isHighlighted ? 'highlighted-timeline' : ''}`}
      ref={highlightRef}
    >
      <div className="classroom-name person-name">
        <span>{person.name}</span>
      </div>
      <div className="time-bars">
        {person.timeSegments.map((segment, index) => {
          // Calculate position and width
          const startDate = segment.startTime;
          const endDate = segment.endTime;
          
          const startHour = startDate.getHours();
          const startMinute = startDate.getMinutes();
          const endHour = endDate.getHours();
          const endMinute = endDate.getMinutes();
          
          const startMinutesSinceRangeStart = (startHour - timeRange.start.getHours()) * 60 + startMinute;
          const endMinutesSinceRangeStart = (endHour - timeRange.start.getHours()) * 60 + endMinute;
          
          const startPercent = Math.max(0, startMinutesSinceRangeStart / totalMinutes);
          const endPercent = Math.min(1, endMinutesSinceRangeStart / totalMinutes);
          const widthPercent = (endPercent - startPercent) * 100;
          
          // Create time description for tooltip
          const timeDesc = `${segment.startTime.toLocaleTimeString()} - ${segment.endTime.toLocaleTimeString()}: ${segment.status}`;
          const prevDesc = segment.prevClassroom ? ` | From: ${segment.prevClassroom.name}` : '';
          const nextDesc = segment.nextClassroom ? ` | To: ${segment.nextClassroom.name}` : '';
          const tooltipText = `${timeDesc}${prevDesc}${nextDesc}`;
          
          //ids for classroom transitions
          const prevClassroomIsOther = sections.find(section => section.name.toLocaleLowerCase() == 'other')
                                          ?.classrooms.some(classroom => classroom.id == segment.prevClassroom?.id);
          const nextClassroomIsOther = sections.find(section => section.name.toLocaleLowerCase() == 'other')
                                          ?.classrooms.some(classroom => classroom.id == segment.nextClassroom?.id);

          const prevClassroomId = segment.prevClassroom ? `${prevClassroomIsOther ? 'other-' :'' }${segment.prevClassroom.id}` : '';
          const nextClassroomId = segment.nextClassroom ? `${nextClassroomIsOther ? 'other-' :'' }${segment.nextClassroom.id}` : '';

          return (
            <div
              key={index}
              className={`time-bar status-${segment.status}`}
              style={{
                left: `${startPercent * 100}%`,
                width: `${widthPercent}%`
              }}
              title={tooltipText}
            >
              {person.role === 'teacher' && segment.prevClassroom && 
                segment.status !== 'break' && segment.prevClassroom.status !== 'break' && (
                <div 
                  className="classroom-transition prev-classroom"
                  onClick={() => handleTransitionClick(prevClassroomId, person.id)}
                  style={{ cursor: 'pointer' }}
                  title={`Click to navigate to ${segment.prevClassroom.name}`}
                >
                  {segment.prevClassroom.name} &rarr;
                </div>
              )}
              
              {person.role === 'teacher' && segment.nextClassroom && 
                segment.status !== 'break' && segment.nextClassroom.status !== 'break' && (
                <div 
                  className="classroom-transition next-classroom"
                  onClick={() => handleTransitionClick(nextClassroomId || '', person.id)}
                  style={{ cursor: 'pointer' }}
                  title={`Click to navigate to ${segment.nextClassroom.name}`}
                >
                  &rarr; {segment.nextClassroom.name} 
                </div>
              )}
            </div>
          );
        })}
        
        {/* Add shift markers */}
        {person.shiftMarkers && (
          <>
            {/* Render start markers */}
            {person.shiftMarkers.starts.map((marker, index) => (
              <div 
                key={`start-${index}`}
                className={`shift-marker start-marker ${marker.isEstimated ? 'estimated' : ''}`}
                style={{ left: `${calculateMarkerPosition(marker.time)}%` }}
                title={`${marker.isEstimated ? 'Estimated ' : ''}Shift start: ${marker.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
              />
            ))}
            
            {/* Render end markers */}
            {person.shiftMarkers.ends.map((marker, index) => (
              <div 
                key={`end-${index}`}
                className={`shift-marker end-marker ${marker.isEstimated ? 'estimated' : ''}`}
                style={{ left: `${calculateMarkerPosition(marker.time)}%` }}
                title={`${marker.isEstimated ? 'Estimated ' : ''}Shift end: ${marker.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default IndividualTimeline;
